// src/components/property/OriginalImagesDialog.tsx
/**
 * OriginalImagesDialog Component
 *
 * Modal dialog for managing original images with drag-and-drop reorder
 * and exclude/restore functionality.
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDndSensors } from '@/hooks/useDndSensors';
import { api } from '../../../convex/_generated/api';
import { X, GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ExcludedImagesSection } from './ExcludedImagesSection';
import type { IOriginalImagesDialogProps } from '@/@types/property';
import type { Id } from '../../../convex/_generated/dataModel';

interface ISortableOriginalImageProps {
  id: string;
  storageId: Id<'_storage'>;
  index: number;
  onExclude: () => void;
}

function SortableOriginalImage({ id, storageId, index, onExclude }: ISortableOriginalImageProps) {
  const imageUrl = useQuery(api.storage.getUrl, { storageId });

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : ('auto' as const),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'relative group rounded-lg overflow-hidden bg-muted aspect-video',
        'border-2 border-transparent hover:border-primary transition-colors'
      )}
    >
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 z-10 p-1 rounded bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-grab"
      >
        <GripVertical className="h-4 w-4" />
      </div>

      {/* Exclude button */}
      <button
        onClick={e => {
          e.stopPropagation();
          onExclude();
        }}
        className="absolute top-2 right-2 z-10 p-1 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 hover:bg-red-500 transition-all"
        aria-label="Exclude image"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Index badge */}
      <div className="absolute bottom-2 left-2 z-10 px-2 py-1 rounded bg-black/70 text-white text-xs font-medium">
        {index + 1}
      </div>

      {/* Image */}
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={`Original property image ${index + 1}`}
          className="w-full h-full object-cover"
          draggable={false}
        />
      ) : (
        <Skeleton className="w-full h-full" />
      )}
    </div>
  );
}

function OriginalImagesDialog({
  open,
  onOpenChange,
  imageIds,
  excludedIndices,
  onExclude,
  onRestore,
  onReorder,
}: IOriginalImagesDialogProps) {
  const sensors = useDndSensors();

  // Memoize excludedSet to prevent unnecessary re-renders
  const excludedSet = useMemo(() => new Set(excludedIndices), [excludedIndices]);

  // Filter to active (non-excluded) images
  const activeImages = useMemo(
    () =>
      imageIds.map((id, index) => ({ id, index })).filter(({ index }) => !excludedSet.has(index)),
    [imageIds, excludedSet]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = activeImages.findIndex(({ id }) => id.toString() === active.id);
      const newIndex = activeImages.findIndex(({ id }) => id.toString() === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newActiveImages = arrayMove(activeImages, oldIndex, newIndex);
        // Reconstruct full imageIds array with excluded images in place
        const newImageIds = [...imageIds];
        let activeIdx = 0;
        for (let i = 0; i < newImageIds.length; i++) {
          if (!excludedSet.has(i)) {
            newImageIds[i] = newActiveImages[activeIdx].id;
            activeIdx++;
          }
        }
        onReorder(newImageIds);
      }
    },
    [activeImages, imageIds, excludedSet, onReorder]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[95vw] !max-w-[95vw] h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Original Images</DialogTitle>
          <DialogDescription>
            Drag to reorder images or click X to exclude them from video generation.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          {/* Active Images Grid with Drag-and-Drop */}
          {activeImages.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={activeImages.map(({ id }) => id.toString())}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4 mt-4">
                  {activeImages.map(({ id, index }) => (
                    <SortableOriginalImage
                      key={id.toString()}
                      id={id.toString()}
                      storageId={id}
                      index={index}
                      onExclude={() => onExclude(index)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              All images have been excluded
            </div>
          )}

          {/* Excluded Images Section */}
          <ExcludedImagesSection
            imageIds={imageIds}
            excludedIndices={excludedIndices}
            onRestore={onRestore}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { OriginalImagesDialog };
